import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import {
  FileText,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Copy,
  MessageCircle,
  Send,
  AlertCircle,
  CheckCircle,
  Clock,
  Megaphone,
  ShieldCheck,
  Bell,
  BookTemplate,
  Variable,
  Sparkles,
  Eye,
  ChevronRight,
  Info,
  Building2,
  Users,
  Globe,
  Search
} from "lucide-react";

const TEMPLATE_CATEGORIES = [
  { id: "utility", label: "Utilidad", description: "Confirmaciones, actualizaciones, recordatorios", icon: Bell, color: "blue" },
  { id: "marketing", label: "Marketing", description: "Promociones, ofertas, novedades", icon: Megaphone, color: "purple" },
  { id: "authentication", label: "Autenticación", description: "Códigos de verificación, OTP", icon: ShieldCheck, color: "green" }
];

const TEMPLATE_STATUS = {
  draft: { label: "Borrador", color: "slate" },
  pending: { label: "Pendiente", color: "amber" },
  approved: { label: "Aprobada", color: "green" },
  rejected: { label: "Rechazada", color: "red" }
};

// Available triggers/variables for WhatsApp templates
const AVAILABLE_TRIGGERS = [
  { id: "{{nombre}}", label: "Nombre del contacto", description: "Nombre del destinatario", example: "Juan" },
  { id: "{{apellido}}", label: "Apellido del contacto", description: "Apellido del destinatario", example: "Pérez" },
  { id: "{{nombre_completo}}", label: "Nombre completo", description: "Nombre y apellido", example: "Juan Pérez" },
  { id: "{{telefono}}", label: "Teléfono", description: "Número de teléfono", example: "809-555-1234" },
  { id: "{{empresa}}", label: "Empresa", description: "Nombre de la empresa", example: "QAsesores" },
  { id: "{{fecha}}", label: "Fecha actual", description: "Fecha del día", example: "06/03/2026" },
  { id: "{{hora}}", label: "Hora actual", description: "Hora del envío", example: "10:30 AM" },
  { id: "{{numero_poliza}}", label: "Número de póliza", description: "Número de la póliza del cliente", example: "POL-2026-001" },
  { id: "{{monto}}", label: "Monto", description: "Monto a pagar o valor", example: "RD$15,000.00" },
  { id: "{{fecha_vencimiento}}", label: "Fecha vencimiento", description: "Fecha de vencimiento", example: "15/03/2026" },
  { id: "{{codigo_verificacion}}", label: "Código de verificación", description: "Código OTP o verificación", example: "123456" },
  { id: "{{link}}", label: "Enlace/URL", description: "Link o URL", example: "https://qasesores.com/pago" },
  { id: "{{ejecutivo}}", label: "Ejecutivo asignado", description: "Nombre del ejecutivo", example: "María González" },
  { id: "{{sucursal}}", label: "Sucursal", description: "Nombre de la sucursal", example: "Santiago" },
  { id: "{{producto}}", label: "Producto/Servicio", description: "Nombre del producto o servicio", example: "Seguro de Vida" },
  { id: "{{numero_ticket}}", label: "Número de ticket", description: "Número de ticket de soporte", example: "TKT-2026-0542" },
  { id: "{{estado}}", label: "Estado", description: "Estado actual", example: "En proceso" },
  { id: "{{dias_restantes}}", label: "Días restantes", description: "Días para vencimiento", example: "5 días" },
  { id: "{{descuento}}", label: "Descuento", description: "Porcentaje o monto de descuento", example: "15%" },
  { id: "{{promocion}}", label: "Promoción", description: "Nombre de la promoción", example: "Verano 2026" }
];

// Predefined templates organized by category
const PREDEFINED_TEMPLATES = {
  utility: [
    {
      name: "recordatorio_pago",
      title: "Recordatorio de Pago",
      body_text: "Hola {{nombre}}, le recordamos que su póliza {{numero_poliza}} vence el {{fecha_vencimiento}}. El monto a pagar es {{monto}}. Puede realizar su pago en: {{link}}\n\nSi tiene alguna duda, contacte a su ejecutivo {{ejecutivo}}.",
      description: "Recordatorio de pago de póliza"
    },
    {
      name: "confirmacion_pago",
      title: "Confirmación de Pago",
      body_text: "¡Gracias {{nombre}}! Hemos recibido su pago de {{monto}} para la póliza {{numero_poliza}}. Su póliza está vigente hasta el {{fecha_vencimiento}}.\n\nQAsesores - Su tranquilidad, nuestra prioridad.",
      description: "Confirmación de pago recibido"
    },
    {
      name: "cita_agendada",
      title: "Cita Agendada",
      body_text: "Hola {{nombre}}, su cita ha sido confirmada para el {{fecha}} a las {{hora}} en nuestra sucursal {{sucursal}}. Le atenderá {{ejecutivo}}.\n\nDirección: {{link}}\n\n¡Le esperamos!",
      description: "Confirmación de cita programada"
    },
    {
      name: "estado_ticket",
      title: "Actualización de Ticket",
      body_text: "Hola {{nombre}}, su ticket {{numero_ticket}} ha sido actualizado.\n\nNuevo estado: {{estado}}\n\nSi necesita más información, puede responder a este mensaje.",
      description: "Actualización de estado de ticket"
    },
    {
      name: "vencimiento_proximo",
      title: "Aviso de Vencimiento Próximo",
      body_text: "Estimado/a {{nombre_completo}},\n\nSu póliza {{numero_poliza}} de {{producto}} vencerá en {{dias_restantes}}.\n\nRenueve ahora para mantener su cobertura activa. Contáctenos al {{telefono}} o visite {{link}}",
      description: "Aviso de póliza próxima a vencer"
    },
    {
      name: "documentos_listos",
      title: "Documentos Listos",
      body_text: "Hola {{nombre}}, sus documentos de {{producto}} ya están listos para retirar en nuestra sucursal {{sucursal}}.\n\nHorario: Lunes a Viernes 8:00 AM - 5:00 PM\n\nRecuerde traer su identificación.",
      description: "Notificación de documentos listos"
    },
    {
      name: "encuesta_satisfaccion",
      title: "Encuesta de Satisfacción",
      body_text: "Hola {{nombre}}, gracias por confiar en nosotros.\n\n¿Podría tomarse un momento para calificar nuestro servicio? Su opinión es muy importante.\n\nEncuesta: {{link}}\n\n¡Gracias!",
      description: "Solicitud de encuesta NPS"
    }
  ],
  marketing: [
    {
      name: "promocion_general",
      title: "Promoción General",
      body_text: "¡Hola {{nombre}}! 🎉\n\nTenemos una promoción especial para ti: {{promocion}}\n\n✨ {{descuento}} de descuento en {{producto}}\n\nVálido hasta: {{fecha_vencimiento}}\n\nMás información: {{link}}",
      description: "Promoción general con descuento"
    },
    {
      name: "nuevo_producto",
      title: "Nuevo Producto",
      body_text: "Hola {{nombre}}, tenemos excelentes noticias.\n\nPresentamos: {{producto}}\n\nBeneficios exclusivos para clientes como tú. Conoce más en: {{link}}\n\nContacta a {{ejecutivo}} para asesoría personalizada.",
      description: "Anuncio de nuevo producto"
    },
    {
      name: "renovacion_especial",
      title: "Oferta de Renovación",
      body_text: "Estimado/a {{nombre_completo}},\n\nComo cliente preferido, le ofrecemos {{descuento}} de descuento al renovar su póliza {{numero_poliza}}.\n\nOferta válida por {{dias_restantes}}.\n\nRenueve aquí: {{link}}",
      description: "Oferta especial para renovación"
    },
    {
      name: "cumpleanos",
      title: "Felicitación de Cumpleaños",
      body_text: "¡Feliz cumpleaños {{nombre}}! 🎂\n\nEn QAsesores celebramos tu día especial con un regalo:\n\n{{descuento}} de descuento en tu próxima compra.\n\nUsa el código: CUMPLE2026\n\nVálido hasta: {{fecha_vencimiento}}",
      description: "Felicitación de cumpleaños con promoción"
    },
    {
      name: "referidos",
      title: "Programa de Referidos",
      body_text: "Hola {{nombre}}, ¿conoces a alguien que necesite protección?\n\nRefiere a un amigo y ambos reciben {{descuento}} de descuento.\n\nComparte este link: {{link}}\n\n¡Entre más refieras, más ganas!",
      description: "Invitación a programa de referidos"
    },
    {
      name: "newsletter",
      title: "Boletín Informativo",
      body_text: "Hola {{nombre}},\n\nNovedades de {{empresa}} - {{fecha}}:\n\n📌 {{producto}}\n\nLee más en: {{link}}\n\nSíguenos para más actualizaciones.",
      description: "Boletín o newsletter"
    }
  ],
  authentication: [
    {
      name: "codigo_otp",
      title: "Código de Verificación",
      body_text: "Tu código de verificación de {{empresa}} es: {{codigo_verificacion}}\n\nEste código expira en 10 minutos.\n\nSi no solicitaste este código, ignora este mensaje.",
      description: "Código OTP para verificación"
    },
    {
      name: "verificacion_cuenta",
      title: "Verificación de Cuenta",
      body_text: "Hola {{nombre}}, para verificar tu cuenta ingresa el código: {{codigo_verificacion}}\n\nO haz clic aquí: {{link}}\n\nEste enlace expira en 24 horas.",
      description: "Verificación de cuenta nueva"
    },
    {
      name: "cambio_contrasena",
      title: "Cambio de Contraseña",
      body_text: "Hola {{nombre}}, recibimos una solicitud para cambiar tu contraseña.\n\nCódigo de seguridad: {{codigo_verificacion}}\n\nSi no fuiste tú, contacta a soporte inmediatamente.",
      description: "Verificación de cambio de contraseña"
    },
    {
      name: "acceso_nuevo_dispositivo",
      title: "Nuevo Dispositivo",
      body_text: "Hola {{nombre}}, detectamos un inicio de sesión desde un nuevo dispositivo el {{fecha}} a las {{hora}}.\n\nSi fuiste tú, ignora este mensaje.\nSi no reconoces esta actividad, cambia tu contraseña: {{link}}",
      description: "Alerta de nuevo dispositivo"
    }
  ]
};

export default function WhatsAppPlantillasPage() {
  const [templates, setTemplates] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  const [formData, setFormData] = useState({
    config_id: "",
    name: "",
    category: "utility",
    language: "es",
    body_text: "",
    parameters: [],
    departamentos_ids: [],
    usuarios_ids: []
  });

  const [paramInput, setParamInput] = useState("");
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [showTriggersDialog, setShowTriggersDialog] = useState(false);
  const [selectedGalleryCategory, setSelectedGalleryCategory] = useState("utility");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, configsRes, deptRes, usersRes] = await Promise.all([
        axios.get(`${API}/whatsapp/templates`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/config`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders())
      ]);
      setTemplates(templatesRes.data || []);
      setConfigs(configsRes.data || []);
      setDepartamentos(deptRes.data || []);
      setUsuarios(usersRes.data || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      config_id: configs[0]?.id || "",
      name: "",
      category: "utility",
      language: "es",
      body_text: "",
      parameters: [],
      departamentos_ids: [],
      usuarios_ids: []
    });
    setParamInput("");
    setUserSearch("");
    setEditingTemplate(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      config_id: template.config_id || "",
      name: template.name || "",
      category: template.category || "utility",
      language: template.language || "es",
      body_text: template.body_text || "",
      parameters: template.parameters || [],
      departamentos_ids: template.departamentos_ids || [],
      usuarios_ids: template.usuarios_ids || []
    });
    setUserSearch("");
    setShowDialog(true);
  };

  const toggleDept = (deptId) => {
    setFormData(prev => ({
      ...prev,
      departamentos_ids: prev.departamentos_ids.includes(deptId)
        ? prev.departamentos_ids.filter(id => id !== deptId)
        : [...prev.departamentos_ids, deptId]
    }));
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      usuarios_ids: prev.usuarios_ids.includes(userId)
        ? prev.usuarios_ids.filter(id => id !== userId)
        : [...prev.usuarios_ids, userId]
    }));
  };

  const applyPredefinedTemplate = (template, category) => {
    setFormData({
      config_id: configs[0]?.id || "",
      name: template.name,
      category: category,
      language: "es",
      body_text: template.body_text,
      parameters: []
    });
    setShowGalleryDialog(false);
    setShowDialog(true);
  };

  const insertTrigger = (trigger) => {
    const textarea = document.getElementById("body_text_textarea");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.body_text;
      const newText = text.substring(0, start) + trigger + text.substring(end);
      setFormData({ ...formData, body_text: newText });
      // Reset cursor position after React re-render
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + trigger.length, start + trigger.length);
      }, 0);
    } else {
      setFormData({ ...formData, body_text: formData.body_text + trigger });
    }
  };

  const openPreview = (template) => {
    setPreviewTemplate(template);
    setShowPreviewDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.body_text || !formData.config_id) {
      toast.error("Nombre, cuerpo del mensaje y configuración son requeridos");
      return;
    }

    // Validate template name (lowercase, underscores, no spaces)
    const validName = /^[a-z0-9_]+$/.test(formData.name);
    if (!validName) {
      toast.error("El nombre solo puede contener letras minúsculas, números y guiones bajos");
      return;
    }

    try {
      setSaving(true);
      if (editingTemplate) {
        await axios.put(`${API}/whatsapp/templates/${editingTemplate.id}`, formData, getAuthHeaders());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/whatsapp/templates`, formData, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm("¿Está seguro de eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/whatsapp/templates/${templateId}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const copyTemplate = (template) => {
    navigator.clipboard.writeText(template.body_text);
    toast.success("Plantilla copiada al portapapeles");
  };

  const addParameter = () => {
    if (paramInput.trim()) {
      const newParam = {
        name: paramInput.trim(),
        type: "text",
        example: ""
      };
      setFormData({
        ...formData,
        parameters: [...formData.parameters, newParam]
      });
      setParamInput("");
    }
  };

  const removeParameter = (index) => {
    setFormData({
      ...formData,
      parameters: formData.parameters.filter((_, i) => i !== index)
    });
  };

  const updateParameter = (index, field, value) => {
    const newParams = [...formData.parameters];
    newParams[index] = { ...newParams[index], [field]: value };
    setFormData({ ...formData, parameters: newParams });
  };

  const insertVariable = (varNum) => {
    const textarea = document.getElementById("template-body");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.body_text;
      const newText = text.substring(0, start) + `{{${varNum}}}` + text.substring(end);
      setFormData({ ...formData, body_text: newText });
    }
  };

  const getConfigName = (configId) => {
    const config = configs.find(c => c.id === configId);
    return config?.name || "Sin configuración";
  };

  const getCategoryInfo = (categoryId) => {
    return TEMPLATE_CATEGORIES.find(c => c.id === categoryId) || TEMPLATE_CATEGORIES[0];
  };

  const getStatusInfo = (status) => {
    return TEMPLATE_STATUS[status] || TEMPLATE_STATUS.draft;
  };

  const countVariables = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-plantillas-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Plantillas de Mensajes
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestiona plantillas de WhatsApp Business
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowTriggersDialog(true)}
                className="gap-2"
              >
                <Variable className="w-4 h-4" />
                Ver Detonadores
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowGalleryDialog(true)}
                className="gap-2"
              >
                <BookTemplate className="w-4 h-4" />
                Galería de Plantillas
              </Button>
              <Button 
                onClick={openNewDialog}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                data-testid="new-template-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Plantilla
              </Button>
            </div>
          )}
        </div>

        {/* Category Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {TEMPLATE_CATEGORIES.map((category) => {
            const count = templates.filter(t => t.category === category.id).length;
            const CategoryIcon = category.icon;
            return (
              <Card key={category.id} className={`hover:shadow-md transition-shadow border-${category.color}-200 bg-${category.color}-50/50 dark:bg-${category.color}-900/20`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-${category.color}-100 dark:bg-${category.color}-900/30 flex items-center justify-center`}>
                      <CategoryIcon className={`w-5 h-5 text-${category.color}-600`} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{category.label}</p>
                      <p className="text-sm text-slate-500">
                        {count} plantilla{count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">{category.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Banner */}
        <Card className="mb-8 border-blue-200 bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Nota:</strong> Las plantillas de WhatsApp Business deben ser aprobadas por Meta antes de poder usarse para mensajes iniciados por la empresa. 
                  Los mensajes de respuesta (dentro de 24h) no requieren plantilla.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : configs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin configuración de WhatsApp</h3>
              <p className="text-slate-500 mb-4">Primero configura un número de WhatsApp</p>
              <Button onClick={() => window.location.href = "/whatsapp/configuracion"} variant="outline">
                Ir a Configuración
              </Button>
            </CardContent>
          </Card>
        ) : templates.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No hay plantillas</h3>
              <p className="text-slate-500 mb-4">Crea tu primera plantilla de mensaje</p>
              {isAdmin && (
                <Button onClick={openNewDialog} className="bg-indigo-500 hover:bg-indigo-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Plantilla
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {templates.map((template) => {
              const categoryInfo = getCategoryInfo(template.category);
              const statusInfo = getStatusInfo(template.status);
              const CategoryIcon = categoryInfo.icon;
              const varCount = countVariables(template.body_text);
              
              return (
                <Card 
                  key={template.id} 
                  className="hover:shadow-lg transition-shadow"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-${categoryInfo.color}-100 dark:bg-${categoryInfo.color}-900/30 flex items-center justify-center`}>
                          <CategoryIcon className={`w-5 h-5 text-${categoryInfo.color}-600`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-mono">{template.name}</CardTitle>
                          <p className="text-sm text-slate-500">{categoryInfo.label}</p>
                        </div>
                      </div>
                      <Badge variant={template.status === "approved" ? "default" : "secondary"} className={
                        template.status === "approved" ? "bg-green-500" : 
                        template.status === "pending" ? "bg-amber-500" :
                        template.status === "rejected" ? "bg-red-500" : ""
                      }>
                        {statusInfo.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Config & Language */}
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {getConfigName(template.config_id)}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {template.language?.toUpperCase() || "ES"}
                        </Badge>
                        {varCount > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {varCount} variable{varCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>

                      {/* Visibility */}
                      <div className="flex flex-wrap items-center gap-1" data-testid={`template-scope-${template.id}`}>
                        {(!template.departamentos_ids?.length && !template.usuarios_ids?.length) ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <Globe className="w-3 h-3 mr-1" /> Visible para todos
                          </Badge>
                        ) : (
                          <>
                            {(template.departamentos_ids || []).map(id => {
                              const d = departamentos.find(x => x.id === id);
                              return d ? (
                                <Badge key={id} className="bg-blue-500 hover:bg-blue-600 text-xs">
                                  <Building2 className="w-3 h-3 mr-1" />{d.name}
                                </Badge>
                              ) : null;
                            })}
                            {(template.usuarios_ids || []).map(id => {
                              const u = usuarios.find(x => x.id === id);
                              return u ? (
                                <Badge key={id} variant="secondary" className="text-xs">
                                  <Users className="w-3 h-3 mr-1" />{u.name || u.email}
                                </Badge>
                              ) : null;
                            })}
                          </>
                        )}
                      </div>

                      {/* Message Preview */}
                      <div 
                        className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={() => openPreview(template)}
                      >
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3 whitespace-pre-line">
                          {template.body_text}
                        </p>
                      </div>

                      {/* Actions */}
                      {isAdmin && (
                        <div className="flex gap-2 pt-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => openEditDialog(template)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => copyTemplate(template)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(template.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? "Editar Plantilla" : "Nueva Plantilla de Mensaje"}
              </DialogTitle>
              <DialogDescription>
                Las plantillas permiten enviar mensajes estructurados a clientes
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Configuración de WhatsApp *</Label>
                  <Select
                    value={formData.config_id}
                    onValueChange={(v) => setFormData({...formData, config_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {configs.map((config) => (
                        <SelectItem key={config.id} value={config.id}>
                          {config.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Categoría *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => setFormData({...formData, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre de la plantilla *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})}
                    placeholder="nombre_plantilla"
                    className="font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Solo minúsculas, números y guiones bajos
                  </p>
                </div>

                <div>
                  <Label>Idioma</Label>
                  <Select
                    value={formData.language}
                    onValueChange={(v) => setFormData({...formData, language: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Español (ES)</SelectItem>
                      <SelectItem value="en">English (EN)</SelectItem>
                      <SelectItem value="pt">Português (PT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Cuerpo del mensaje *</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((num) => (
                      <Button
                        key={num}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-6 px-2"
                        onClick={() => insertVariable(num)}
                      >
                        {`{{${num}}}`}
                      </Button>
                    ))}
                  </div>
                </div>
                <Textarea
                  id="body_text_textarea"
                  value={formData.body_text}
                  onChange={(e) => setFormData({...formData, body_text: e.target.value})}
                  placeholder="Hola {{nombre}}, tu cita para {{producto}} está confirmada para el {{fecha}}."
                  rows={5}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Usa detonadores como {`{{nombre}}`}, {`{{fecha}}`}, etc. {formData.body_text.length}/1024 caracteres
                </p>
              </div>

              {/* Quick Triggers Panel */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Variable className="w-4 h-4 text-purple-500" />
                    Detonadores Rápidos
                  </Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowTriggersDialog(true)}
                  >
                    Ver todos
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {AVAILABLE_TRIGGERS.slice(0, 10).map((trigger) => (
                    <Button
                      key={trigger.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 px-2 font-mono"
                      onClick={() => insertTrigger(trigger.id)}
                      title={trigger.description}
                    >
                      {trigger.id}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Variables Preview */}
              {countVariables(formData.body_text) > 0 && (
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs font-medium text-slate-600 mb-2">Variables detectadas:</p>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: countVariables(formData.body_text) }, (_, i) => (
                      <Badge key={i} variant="outline">
                        {`{{${i + 1}}}`} - Variable {i + 1}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Visibility Scoping */}
              <div className="border rounded-lg p-3 space-y-3" data-testid="template-visibility-section">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-indigo-500" />
                  <Label className="text-sm font-semibold">¿Quién puede ver esta plantilla?</Label>
                </div>
                <p className="text-xs text-slate-500 flex items-start gap-1">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Si no seleccionas nada, la plantilla estará visible para <strong>todos</strong>. Si eliges departamentos y/o usuarios, solo ellos la verán al iniciar una conversación.
                </p>

                {formData.departamentos_ids.length === 0 && formData.usuarios_ids.length === 0 && (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                    <Globe className="w-3 h-3 mr-1" /> Visible para todos
                  </Badge>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Departments */}
                  <div>
                    <Label className="text-xs flex items-center gap-1 mb-1">
                      <Building2 className="w-3.5 h-3.5" /> Departamentos
                    </Label>
                    <ScrollArea className="h-40 border rounded-md p-2">
                      {departamentos.length === 0 ? (
                        <p className="text-xs text-slate-400 p-2">No hay departamentos</p>
                      ) : (
                        departamentos.map(d => (
                          <label key={d.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm" data-testid={`tpl-dept-${d.id}`}>
                            <Checkbox
                              checked={formData.departamentos_ids.includes(d.id)}
                              onCheckedChange={() => toggleDept(d.id)}
                            />
                            <span>{d.name}</span>
                          </label>
                        ))
                      )}
                    </ScrollArea>
                  </div>

                  {/* Users */}
                  <div>
                    <Label className="text-xs flex items-center gap-1 mb-1">
                      <Users className="w-3.5 h-3.5" /> Usuarios
                    </Label>
                    <div className="relative mb-1">
                      <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Buscar usuario..."
                        className="pl-7 h-8 text-xs"
                      />
                    </div>
                    <ScrollArea className="h-[118px] border rounded-md p-2">
                      {usuarios
                        .filter(u => {
                          if (!userSearch) return true;
                          const s = userSearch.toLowerCase();
                          return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
                        })
                        .map(u => (
                          <label key={u.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm" data-testid={`tpl-user-${u.id}`}>
                            <Checkbox
                              checked={formData.usuarios_ids.includes(u.id)}
                              onCheckedChange={() => toggleUser(u.id)}
                            />
                            <span className="truncate">{u.name || u.email}</span>
                          </label>
                        ))}
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTemplate ? "Guardar Cambios" : "Crear Plantilla"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-mono">{previewTemplate?.name}</DialogTitle>
              <DialogDescription>
                Vista previa de la plantilla
              </DialogDescription>
            </DialogHeader>

            {previewTemplate && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge>{getCategoryInfo(previewTemplate.category).label}</Badge>
                  <Badge variant="outline">{previewTemplate.language?.toUpperCase()}</Badge>
                  <Badge variant={previewTemplate.status === "approved" ? "default" : "secondary"}>
                    {getStatusInfo(previewTemplate.status).label}
                  </Badge>
                </div>

                {/* WhatsApp-style preview */}
                <div className="bg-[#e5ddd5] dark:bg-slate-800 rounded-lg p-4">
                  <div className="bg-white dark:bg-slate-700 rounded-lg p-3 shadow-sm max-w-[80%]">
                    <p className="text-sm whitespace-pre-line">{previewTemplate.body_text}</p>
                    <p className="text-xs text-slate-400 text-right mt-1">12:00 ✓✓</p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                Cerrar
              </Button>
              <Button onClick={() => previewTemplate && copyTemplate(previewTemplate)}>
                <Copy className="w-4 h-4 mr-2" />
                Copiar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gallery Dialog */}
        <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
          <DialogContent className="max-w-4xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookTemplate className="w-5 h-5 text-indigo-600" />
                Galería de Plantillas Predefinidas
              </DialogTitle>
              <DialogDescription>
                Seleccione una plantilla para usarla como base. Puede modificarla después.
              </DialogDescription>
            </DialogHeader>

            <Tabs value={selectedGalleryCategory} onValueChange={setSelectedGalleryCategory}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="utility" className="gap-2">
                  <Bell className="w-4 h-4" />
                  Utilidad ({PREDEFINED_TEMPLATES.utility.length})
                </TabsTrigger>
                <TabsTrigger value="marketing" className="gap-2">
                  <Megaphone className="w-4 h-4" />
                  Marketing ({PREDEFINED_TEMPLATES.marketing.length})
                </TabsTrigger>
                <TabsTrigger value="authentication" className="gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Autenticación ({PREDEFINED_TEMPLATES.authentication.length})
                </TabsTrigger>
              </TabsList>

              {Object.entries(PREDEFINED_TEMPLATES).map(([category, templates]) => (
                <TabsContent key={category} value={category} className="mt-4">
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {templates.map((template) => (
                        <Card key={template.name} className="hover:shadow-md transition-shadow cursor-pointer group">
                          <CardHeader className="pb-2">
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-base">{template.title}</CardTitle>
                                <p className="text-xs text-slate-500 font-mono">{template.name}</p>
                              </div>
                              <Button 
                                size="sm" 
                                onClick={() => applyPredefinedTemplate(template, category)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                Usar
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">{template.description}</p>
                            <div className="bg-slate-50 dark:bg-slate-800 rounded p-2 text-xs font-mono whitespace-pre-wrap line-clamp-4">
                              {template.body_text}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>
              ))}
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGalleryDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Triggers Dialog */}
        <Dialog open={showTriggersDialog} onOpenChange={setShowTriggersDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Variable className="w-5 h-5 text-purple-600" />
                Detonadores / Variables Disponibles
              </DialogTitle>
              <DialogDescription>
                Use estos detonadores en sus plantillas. Se reemplazarán automáticamente con los datos del contacto.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                <Info className="w-4 h-4" />
                <span className="text-sm font-medium">Cómo usar los detonadores</span>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                Copie el detonador y péguelo en el cuerpo de su plantilla. Al enviar el mensaje, se reemplazará automáticamente con el valor correspondiente del contacto.
              </p>
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AVAILABLE_TRIGGERS.map((trigger) => (
                  <div 
                    key={trigger.id}
                    className="border rounded-lg p-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <code className="text-sm font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded">
                          {trigger.id}
                        </code>
                        <p className="text-sm font-medium mt-1">{trigger.label}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(trigger.id);
                          toast.success(`Copiado: ${trigger.id}`);
                        }}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">{trigger.description}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Ejemplo: <span className="font-medium">{trigger.example}</span>
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTriggersDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
